import { Handle, Position } from '@xyflow/react'
import { SM_THEME as T } from './stateMachineTheme'
import { SM_PINS } from '../../lib/smPinRouting'

/**
 * smPins — the four connection pins a State Machine node carries (ENG-1072).
 *
 * OUTPUTS are triangles centred on the node's left and right edges, pointing
 * outward — the direction the wire leaves. INPUTS are circles centred on the
 * top and bottom edges, so the four pins sit at the four edge midpoints, the
 * way Florent's reference FSM reads. Which of each pair a given transition uses
 * is decided from node geometry (see lib/smPinRouting), so the wires flip sides
 * live as you drag a node around.
 *
 * The `position` prop is what ReactFlow uses for the spline's TANGENT, not for
 * where the pin is drawn — the anchor point comes from the element's real DOM
 * box. Outputs get Left/Right so wires fan out sideways; inputs get Top/Bottom
 * so they arrive vertically.
 */

// Triangles: a square clipped to a point. `border-radius: 0` overrides
// ReactFlow's default round handle.
const TRIANGLE_SIZE  = 11
const TRIANGLE_LEFT  = 'polygon(100% 0, 100% 100%, 0 50%)'
const TRIANGLE_RIGHT = 'polygon(0 0, 0 100%, 100% 50%)'

const outputStyle = (side) => ({
  background: T.handle,
  border: 'none',
  borderRadius: 0,
  width: TRIANGLE_SIZE,
  height: TRIANGLE_SIZE,
  clipPath: side === 'left' ? TRIANGLE_LEFT : TRIANGLE_RIGHT,
  // Centred on its side and hanging just outside the node body. Centred with a
  // negative margin rather than ReactFlow's `translate(-50%, -50%)` so the
  // element's layout box and its painted box agree.
  top: '50%',
  bottom: 'auto',
  marginTop: -TRIANGLE_SIZE / 2,
  [side]: -9,
  transform: 'none',
})

const inputStyle = {
  background: T.handle,
  border: 'none',
  width: 10,
  height: 10,
}

/** Both output pins — left and right. Used by state and entry nodes. */
export function OutputPins() {
  return (
    <>
      <Handle
        type="source"
        position={Position.Left}
        id={SM_PINS.outLeft}
        style={outputStyle('left')}
      />
      <Handle
        type="source"
        position={Position.Right}
        id={SM_PINS.outRight}
        style={outputStyle('right')}
      />
    </>
  )
}

/** Both input pins — top and bottom. */
export function InputPins() {
  return (
    <>
      <Handle
        type="target"
        position={Position.Top}
        id={SM_PINS.inTop}
        style={inputStyle}
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id={SM_PINS.inBottom}
        style={inputStyle}
      />
    </>
  )
}
